/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */


// API Response Types
export enum APIReasons {
  None = 0,
  InternalException = 10000,
  ArgumentException = 10001,
  InvalidModelState = 10002,
  InvalidEmailOrPassword = 11001,
  EmailIsNotCorrect = 11002,
  EmailWasTaken = 11003,
  PasswordToShort = 11004,
  CannotCreateRoles = 11005,
  EmailNotVerified = 11006,
  VerificationEmailNotSent = 11007,
  UserNotFound = 11008,
  InvalidPassword = 11009,
  YouHaveItems = 11050,
  YouShouldAgreeWithTerms = 11051,
  DataIsAlreadyExist = 20001,
  DataNotFound = 20002,
  CannotCreateCompany = 20004,
  CannotResestPassword = 20005,
  ResestPasswordExpired = 20006,
  InvalidResetPassword = 20007,
  DeclinedPermission = 40001,
  OperationDeclined = 40002,
  UnAuthorized = 40003,
  LimitReached = 40004,
  NeedToBeReleased = 80001,
  BookingWasCanceled = 80101,
  BookingWasApproved = 80102,
  BookingDuplicate = 80103,
  ItemIsBooked = 80104,
  BookingWasDeclined = 80105,
  CarIsNotAvailable = 80106,
  LessThatMinimumDays = 80107,
  CarAlreadyTaken = 80108,
  YouAlreadyContactHost = 80109,
  EmployeeRequestException = 80200,
  AlreadyHired = 80201,
  CannotFindUserWithSpecificEmail = 80206,
  CannotHireYourself = 80207,
  UserAlreadyHasRequest = 80208,
  UserIsRenterOnly = 80209,
  UserHasCars = 80210,
  UserHasEmployees = 80211,
  UserAlreadyHaired = 80212,
  CompanyNotFound = 80213,
  NotFoundEmployee = 80214,
  BookingNotFound = 80215,
  DuplicateDriverLicense = 80300,
  KillswichInvalidCredentials = 90001,
}

// Booking/approve reports refusals *inside* a success envelope (reason stays 0), so callers must read
// isSuccess — see BookingController.ApproveBooking.
export interface ApproveBookingResponse {
  isMonthlyCar: boolean;
  status: number;
  isSuccess: boolean;
  errorDetails?: string;
}

export interface APIResponse<T> {
  result: T;
  reason: APIReasons;
  message: string;
  stackTrace?: string;
  hasMore?: boolean;
  errorCode?: number;
}

// User Types
export interface UserAuthenticationResult {
  token: string;
  tokenExpired: string;
  id: string;
  email: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  nickName?: string;
  phone: string;
  employerId?: string;
  addressLine1?: string;
  addressLine2?: string;
  birthDate?: string;
  imageURL?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  stateId?: string;
  country?: string;
  company?: string;
  isOwner: boolean;
  isRenter: boolean;
  isEmployee: boolean;
  isPartner: boolean;
  isAdmin: boolean;
  isEmployeeRequestAccepted: boolean;
  isCompany: boolean;
}

export interface OwnerDTO {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  stripeConnectedAccountId?: string;
  hasAgreement?: boolean;
  agreementUrl?: string;
  // Holds the Partner role (admin Owners page: green row + partner filter).
  isPartner?: boolean;
  // Holds the Admin role (yellow row).
  isAdmin?: boolean;
}

export interface UserByOwnerDTO {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  isNew: boolean;
  zipCode?: string;
  addressLine1?: string;
  addressLine2?: string;
  state?: string;
  city?: string;
  country?: string;
  dateOfBirth?: string;
  licenseNumber?: string;
  expirationDate?: string;
  isEmailConfirmed?: boolean;
  hasCard?: boolean;
  createdAt?: string;
  /** ISO instant the sign-in lock expires, or null/absent when the account is not locked. */
  lockoutEndUtc?: string | null;
  /** Consecutive failed sign-ins. Non-zero without a lock means the account is close to one. */
  failedLoginAttempts?: number;
}

export interface DriverLicenseDTO {
  userId?: string;
  frontSideImageUrl?: string;  // API returns this field name
  backSideImageUrl?: string;   // API returns this field name
  frontImageUrl?: string;      // Legacy compatibility
  backImageUrl?: string;       // Legacy compatibility
  licenseNumber?: string;
  stateIssued?: string;
  countryIssued?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
  sex?: string;
  height?: string;
  eyeColor?: string;
  dateOfBirth?: string;
  birthDate?: string;          // Legacy compatibility
  issueDate?: string;
  expirationDate?: string;
  licenseAddress?: string;
  licenseCity?: string;
  licenseState?: string;
  licensePostalCode?: string;
  restrictionCode?: string;
  endorsements?: string;
  verificationMethod?: string;
  // Universal / original-language fields (populated by the Claude OCR reader for foreign licenses)
  documentType?: string;
  patronymic?: string;
  firstNameLocal?: string;
  lastNameLocal?: string;
  patronymicLocal?: string;
  nationalIdNumber?: string;
  issuingAuthority?: string;
  issuingAuthorityLocal?: string;
  placeOfBirth?: string;
  placeOfBirthLocal?: string;
  addressLocal?: string;
  categories?: string;
  // Orientation data from Node.js proxy (Google Document AI)
  frontOrientation?: 0 | 90 | 180 | 270;
  backOrientation?: 0 | 90 | 180 | 270;
  frontOrientationConfidence?: number;
  backOrientationConfidence?: number;
  frontOrientationReason?: string;
  backOrientationReason?: string;
}

export enum DocumentType {
  DriverLicense = 0,
  Signature = 1,
  VehicleAgreementDocument = 2,
  AgreementTemplate = 3,
  AgreementDocument = 4,
  // API returns dtd: 18 for vehicle agreements - need to map this
  VehicleRentalAgreementDocument = 18
}

export interface DocumentDTO {
  id: string;
  userId: string;
  role: number; // UserRole enum
  dtd: DocumentType;
  value: string; // URL or Base64-encoded content
  description: string;
  isEmpty: boolean;
  deleted: boolean;
  createdBy: string; // DateTime as string
}

// Car Types
export interface CarType {
  id: string;
  name: string;
}

export interface CarInsurance {
  id: string;
  name: string;
}

export interface City {
  id?: string;
  name: string;
  stateId: string;
}

export interface StateInfo {
  id: string;
  abbreviation: string;
  name?: string;
}

export enum BookingStatus {
  Init = 0,
  PendingApproval = 1,
  CompletedEarly = 2,
  RequestExpired = 3,
  RequestDenied = 4,
  ApprovedWaitingForInsuranceApproval = 5,
  Approved = 6,
  Completed = 7,
  Draft = 8,
  DepositFailed = 9,
  PaymentFailed = 10,
  RefundFailed = 11,
  TollCardMissing = 12,
  NotBooked = 13,
  WaitingNewDriverInfo = 14,
  WaitingForAgreement = 15,
  WaitingForCC = 16,
  Canceled = 17,
  /** Booked, but the car has not been handed over yet (an external fleet platform's "open"/"reserved").
   *  Holds the car like Approved, but the toll pipeline never bills it — nobody is driving. */
  Reserved = 18,
  // Legacy aliases for backward compatibility
  Pending = 1,
  Declined = 4,
}

export enum EnumRentalType {
  FullTime = 2,
  DayPerShift = 4,
  NightPerShift = 8,
}

export enum RequestRole {
  Owner = 0,
  Renter = 1,
}

export interface Car {
  id: string;
  title: string;
  description?: string;
  ownerId: string;
  type: CarType;
  // Vehicle segment/class (SUV, Luxury, …) from CarCategories — a different axis than `type`.
  categoryId?: string;
  categoryName?: string;
  /** Multilingual category name (JSON localized map); rendered per GUI language. */
  categoryNameJson?: string;
  /** Availability status FK (CarStatuses.Id). */
  statusId?: string;
  /** Availability status stable key ("available" | "not"); empty/undefined = available. */
  statusKey?: string;
  /** Availability status display label. */
  statusName?: string;
  imageURL?: string;
  brand?: string;
  model?: string;
  year?: number;
  color?: string;
  interiorColor?: string;
  plateNumber?: string;
  plateNumberState?: StateInfo;
  tlc?: string;
  vin?: string;
  /** Odometer reading (miles); two-way synced with the booking plugin (HQ odometer). */
  mileage?: number;
  address1?: string;
  address2?: string;
  city?: City;
  zip?: string;
  phone?: string;
  latitude?: number;
  longitude?: number;
  priceFullTimePerWeek?: number;
  priceDayPerShiftWeek?: number;
  priceNightPerShiftWeek?: number;
  priceSecurityDeposit?: number;
  // Per-period rate tiers (like HQ daily/weekly/monthly). 0 = tier not offered.
  priceDaily?: number;
  priceWeekly?: number;
  priceMonthly?: number;
  minReservationDays?: number;
  allowNegotiatingPrice?: boolean;
  isPublished?: boolean;
  /** Owner opt-in: list this car on the My E-Z Wheels storefront. Off by default. */
  showInMyEzWheels?: boolean;
  termsAndConditions?: boolean;
  isPedicabCar?: boolean;
  isMonthlyCar?: boolean;
  tollTagAttached?: boolean;
  rentersTag?: boolean;
  requestShipTag?: boolean;
  /** FK to the car's booking-plugin pickup location (OwnerBookingLocations.Id); null = none. */
  bookingLocationId?: string | null;
  /** True when the car is synced from a booking plugin (FleetMgmt) — plugin owns plate/VIN/make/model/
   *  year/color/type/location, so those fields are read-only. Excludes Turo. */
  bookingPluginSourced?: boolean;
  isExternal?: boolean;
  /** Platform a car was imported from (e.g. 'turo'); undefined for native cars. */
  externalSource?: string;
  /** Platform vehicle id (e.g. Turo '3792318') linked to this car — the exact key that resolves booking emails to it. */
  externalVehicleId?: string;
  insurance?: CarInsurance;
  fullCoverageInsurance?: CarInsurance;
  rentalType?: EnumRentalType;
  pricingPeriod?: number; // 0=Week, 1=Day, 2=Month
  // Booking info
  renterFirstName?: string;
  renterLastName?: string;
  renterPhoneNumber?: string;
  renterEmail?: string;
  bookingId?: string;
  bookingStatus?: BookingStatus;
  bookingDateFrom?: string;
  bookingDateTo?: string;
  renterId?: string;
  updated?: string;
  // Payment info
  paymentNote?: string;
  paymentStatus?: boolean;
  // Toll coverage — how many toll agencies the car sits on, and how many of those links still have
  // no transponder number. Drives the row colour and the toll filter on the vehicle lists.
  tollAgencies?: number;
  tollAgenciesWithoutTransponder?: number;
}

export interface CarDetailsRequest {
  id?: string;
  ownerId: string;
  role: RequestRole;
  carTypeId: string;
  rentalType: number;
  priceFullTimePerWeek?: number;
  priceDayPerShiftWeek?: number;
  priceNightPerShiftWeek?: number;
  priceSecurityDeposit?: number;
  // Per-period rate tiers (like HQ daily/weekly/monthly).
  priceDaily?: number;
  priceWeekly?: number;
  priceMonthly?: number;
  allowNegotiatingPrice?: boolean;
  minimumReservationDays?: number;
  pricingPeriod?: number;
  carBrand?: string;
  carModel?: string;
  year?: number;
  color?: string;
  interiorColor?: string;
  liabilityInsuranceId?: string;
  fullCoverageId?: string;
  newLiabilityInsurance?: string;
  newFullCoverage?: string;
  addressLine1?: string;
  addressLine2?: string;
  stateId?: string;
  city?: string;
  zipCode?: string;
  country?: string;
  phoneNumber?: string;
  carPlateNumber?: string;
  carPlateNumberState?: string;
  tlc?: string;
  title?: string;
  description?: string;
  published?: boolean;
  /** Owner opt-in for the My E-Z Wheels storefront. Omit to keep the stored value (backend treats null as "unchanged"). */
  showInMyEzWheels?: boolean;
  termsConditions?: boolean;
  isPedicabCar?: boolean;
  isMonthlyCar?: boolean;
  seaterId?: string;
  engineId?: string;
  killSwitchId?: string;
  vin?: string;
  /** Odometer reading (miles); pushed to the booking plugin (HQ odometer) on save. */
  mileage?: number;
  verraTollTagAttached?: boolean;
  isExternal?: boolean;
  rentersTag?: boolean;
  requestShipTag?: boolean;
  bookingLocationId?: string | null;
  latitude?: number;
  longitude?: number;
}

// Booking Types
export interface BookingsDTO {
  bookingId: string;
  carId: string;
  renterId?: string;
  renterFirstName: string;
  renterLastName: string;
  renterPhoneNumber: string;
  renterEmail: string;
  renterNick?: string;
  renterImage?: string;
  dateFrom: string;
  dateTo: string;
  /**
   * When the rental was actually closed; absent while it is still open. The car is free from this
   * moment rather than from `dateTo`, so gates that mirror the API (car change, overlap) measure from
   * here — see `utils/bookingCarChange.ts`.
   */
  dateComplete?: string;
  status: BookingStatus;
  price?: number;
  minimumReservationDays?: number;
  isApproved?: boolean;
  isCompleted?: boolean;
  rentalType?: EnumRentalType;
  // Car info
  carTitle?: string;
  carModel?: string;
  carPlateNumber?: string;
  carPlateNumberState?: string;
  carBrand?: string;
  carType?: string;
  carYear?: number;
  carImage?: string;
  // Agreement
  agreementDocumentId?: string;
  agreementPdfUrl?: string;
  // Approval flags bitmask (6 conditions for auto-approval)
  approvalFlags?: number;
  /**
   * Total tolls charged against the booking. Historically misnamed on the wire — it has never been a
   * registration fee, and the table has always labelled the column "Toll Amount". Prefer `tollAmount`.
   */
  registrationFee?: number;
  /** Total tolls charged against the booking. Same number as `registrationFee`, honestly named. */
  tollAmount?: number;
  // Legacy English text. Kept for older clients — never compare against it in new code.
  paymentStatusNote?: string;
  // Stable code behind paymentStatusNote (backend: Aegis.Common.Utils.BookingPaymentStatus).
  paymentStatusCode?: string;
  /**
   * Which system the booking was created through (backend: Aegis.Common.Utils.BookingOrigin):
   * 'own' | 'turo' | 'hq' | 'renteon' | 'externalFleet' | 'externalRental'. Absent from a backend
   * older than 3.0.153 — see `utils/bookingOrigin.ts`, which treats that as "unknown", not "ours".
   */
  origin?: string;
  /** English label for `origin`. The UI localizes the code instead; kept for raw displays. */
  originName?: string;
  /** Security deposit actually charged for this booking; 0/undefined when none was taken. */
  depositAmount?: number;
  /** True while a charged deposit is still open — false once refunded, false when never charged. */
  canReturnDeposit?: boolean;
  // Legacy alias
  id?: string;
}

/**
 * One car of a rental with the period it was on it (backend: `Aegis.Back.BusinessServices.BookingCarSegment`).
 *
 * A rental changes vehicle in two shapes and neither is visible on the booking row: an in-place swap keeps
 * one booking and records the cars as intervals, while a mid-rental change made in the portal ends the
 * booking early and continues the rental in a SECOND booking on the new car. The backend re-joins both, so
 * a segment can belong to a different booking than the one that was asked for — `isCurrentBooking` says so.
 */
export interface BookingCarSegment {
  bookingId: string;
  /** False when this stretch lives in the sibling booking a mid-rental change left behind. */
  isCurrentBooking: boolean;
  carId: string;
  /** True for the car the booking carries right now. */
  isCurrentCar: boolean;
  fromUtc: string;
  /** Always present — an open interval comes back clamped to the end of its own booking. */
  toUtc: string;
  carBrand?: string;
  carModel?: string;
  carYear?: number;
  carTitle?: string;
  carPlateNumber?: string;
  carPlateNumberState?: string;
}

export interface BookingDTO {
  id: string;
  carId: string;
  userId: string;
  dateFrom: string;
  dateTo: string;
  status: BookingStatus;
  approvalFlags?: number;
}

// Car availability-status option (from GET /Cars/statuses) — drives the vehicles status filter.
export interface CarStatusOption {
  id: string;
  key: string;
  name: string;
  position: number;
}

// Payment Types
export interface CardDTO {
  brand: string;
  last4: string;
  expirationMonth: number;
  expirationYear: number;
  isActive: boolean;
  stripePaymentMethodId: string;
  /** True when this card lives on a booking plugin's gateway (read-only on our side: no set-active/delete,
   *  and "add card" goes through the plugin's hosted link). */
  pluginManaged?: boolean;
  /** Display label when the plugin doesn't expose brand/last4 (e.g. "Card 1") or a generic placeholder. */
  label?: string;
}

export interface CardView {
  brand: string;
  last4: string;
  expirationMonthYear: string;
  isActive: boolean;
  stripePaymentMethodId: string;
  pluginManaged?: boolean;
  label?: string;
}

export interface ActivePaymentDTO {
  activeId: string;
  ownerId?: string;
  renterId?: string;
  stripePaymentMethodId?: string;
}

export interface VerraTollDailyInvoiceDTO {
  id: string;
  visibleId?: number;
  externalFleetCode?: string;
  fleetName?: string;
  vehicleId?: string;
  vin?: string;
  plateNumber?: string;
  carPlateNumber?: string;
  driverFirstName?: string;
  driverLastName?: string;
  driverPhone?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  driverEmailAddress?: string;
  email?: string;
  tollId?: number;
  tollDate?: string;
  tollTime?: string;
  tollExitDate?: string;
  tollAuthority?: string;
  tollAuthorityDescription?: string;
  transactionType?: string;
  entry?: string;
  exit?: string;
  amount: number;
  adjustedAmount?: number;
  currency?: string;
  dateInvoiceDeployed?: string;
  invoiceDateDeployed?: string;
  infoHeader?: string;
  dailyPaymentId?: string;
  dailyTollId?: string;
  originalPayerId?: string;
  transponderNumber?: string;
  ownerFirstName?: string;
  ownerLastName?: string;
  tag?: boolean;
  bookingId?: string;
  invoiceStatus?: number;
  details?: string;
  // Legacy fields for compatibility
  userId?: string;
  status?: number;
  createdAt?: string;
  paidAt?: string;
  licensePlate?: string;
  tollCompanyId?: string;
  tollCompanyName?: string;
}

export interface OwnerChargeDTO {
  plateNumber: string;
  plateState: string;
  tollAmount: number;
  actualTollAmount: number;
  tollCount: number;
  totalCommission: number;
  paymentAmount: number;
  stripePaymentAmount: number;
  stripeFee: number;
  paymentStatus: number;
  stripePaymentId: string;
  dateCreated: string;
  chargeType: string;
  /** True when the driver was charged through a connected booking plugin's gateway (e.g. HQ) — not our Stripe. */
  collectedViaPlugin?: boolean;
  /** Platform that collected the charge when collectedViaPlugin (e.g. "hqrental"); null for native Stripe. */
  collectedVia?: string | null;
  /** Human-readable provider name when collectedViaPlugin (e.g. "HQ Rental Software"); null for native Stripe. */
  collectedViaName?: string | null;
  /** Driver (renter) the charge belongs to — used to jump to the Drivers page. */
  userId?: string | null;
  /** Booking the charge belongs to — used to jump to the Bookings page. */
  bookingId?: string | null;
  /** Driver full name (People FirstName + LastName). */
  driverName?: string | null;
  /** Driver email (Users.Email) — the most precise key for the Drivers-page search filter. */
  driverEmail?: string | null;
}

export interface PaymentStatusInfoDTO {
  paymentId: string;
  bookingId: string | null;
  invoiceStatus: number;
  invoiceStatusName: string;
  plateNumber: string;
  plateState: string;
  tollAmount: number;
  commission: number;
  total: number;
  tollCount: number;
  dateCreated: string;
  lastUpdate: string | null;

  stripePaymentIntentId: string | null;
  stripeStatus: string | null;
  cardBrand: string | null;
  cardLast4: string | null;
  receiptUrl: string | null;
  stripeCreated: number | null;
  amountReceived: number | null;

  grossChargeAmount: number | null;
  netAmount: number | null;
  stripeFee: number | null;
  chargedAt: string | null;

  errorCode: string | null;
  errorMessage: string | null;
  errorDeclineCode: string | null;
  errorType: string | null;
  errorParam: string | null;

  /**
   * Why the charge failed as OUR side recorded it (TollDailyPayment.Details). The only account of
   * anything that died before Stripe — card suppressed by the failure breaker, no chargeable card, an
   * exception on our side — where the Stripe fields above are all null.
   */
  details: string | null;
}

export interface ChargeDetailDTO {
  tollDate: string;
  tollTime: string;
  authority: string;
  description: string;
  tollType: string;
  entry: string;
  exit: string;
  splitNetAmount: number;
  splitStripePaymentAmount: number;
  stripePaymentId: string;
}

export interface OwnerTollDTO {
  tollDate: string;
  tollTime: string;
  carPlateNumber: string;
  vin: string;
  authority: string;
  description: string;
  amount: number;
  adjustedAmount: number;
  dailyPaymentId: string;
  bookingId: string;
  invoiceStatus: number;
  /** The toll's car â€” names the rental company whose card set pays it (cards are per driver and owner). */
  carId?: string;
  /** The rental company the toll belongs to (owner of the car). */
  ownerId?: string;
}

export interface VerraTollDailyPaymentInfoRequest {
  carPlateNumber: string;
  numberOfTolls: number;
  total: number;
  stripeAdjusted?: string;
  dailyPaymentId: string;
  phone?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  ownerFirstName?: string;
  ownerLastName?: string;
  killSwitchId?: string;
  tag: boolean;
  paymentFlowId?: string;
  /** FK to People.Id / Users.Id — the payer (driver). Serialized from backend UserId. */
  userId?: string;
  invoiceDateDeployed: string;
  vin?: string;
  bookingId?: string;
  invoiceStatus: number;
  paymentStatus: number;
  details?: string;
}

export interface VerraTollDailyPaymentDTO {
  id: string;
  invoiceId: string;
  amount: number;
  status: number;
  createdAt: string;
  stripePaymentIntentId?: string;
}

// Pending payment (from GET /TollPayment/get-pending-payments/{userId})
export interface PendingPaymentDTO {
  id: string;
  paymentFlowId?: string;
  bookingId?: string;
  carId?: string;
  invoiceStatus: number;
  paymentStatus: number;
  dateCreated: string;
}

// Constants
export const Constants = {
  CarSharingId: 'L6F089DA-5CF3-4383-BD8D-BFFFCF18EABC',
  CommercialCarId: 'S6S080DA-3CF3-5383-CD8D-NFMFCF18EAWS',
  TaxiUberLyftId: 'A1B2C3D4-E5F6-7890-ABCD-EF1234567890',
  LicensePlateId: 'E4F089DA-5CF3-4383-BD8D-BFFFCF18ECCC', // TlcPlateId from .NET
  UI: {
    NoCarImageWhite: 'NoCarWhite-2',
    CarImage: '/images/Car.png',
  },
  TollCompanies: {
    'A2C51FED-AE1D-4659-BAFC-7305F6A8B442': 'VERRA TOLL',
    'C9B2CD59-549E-4CB6-B1C3-A007D9B0BE73': 'E-Z Pass NY',
  },
};

// ============================================================
// Toll Distribution Settings Types
// ============================================================

export type TollDistributionMode = 'PlatformOnly' | 'OwnerShare';

export interface TollDistributionSettingDTO {
  id: string;
  userId: string;
  vehicleId?: string;
  vehicleName?: string;
  plateNumber?: string;
  tollSystemName?: string;
  distributionMode: string;
  ownerPercentage: number;
  minimumTransferAmount: number;
  connectedAccountId?: string;
  connectedAccountName?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTollDistributionSettingDTO {
  vehicleId?: string;
  tollSystemName?: string;
  distributionMode: TollDistributionMode;
  ownerPercentage: number;
  minimumTransferAmount: number;
  connectedAccountId?: string;
  isActive: boolean;
}

export interface UpdateTollDistributionSettingDTO {
  distributionMode: TollDistributionMode;
  ownerPercentage: number;
  minimumTransferAmount: number;
  connectedAccountId?: string;
  isActive: boolean;
}

export interface TollDistributionCalculationRequest {
  actualTollAmount: number;
  tollSystemName: string;
  carOwnerId: string;
  vehicleId?: string;
}

export interface TollDistributionCalculationResponse {
  actualTollAmount: number;
  markupAmount: number;
  totalChargeAmount: number;
  ownerAmount: number;
  platformAmount: number;
  transferRequired: boolean;
  reason: string;
  tollSystemName: string;
  distributionMode: string;
  ownerPercentage: number;
}

export interface VehicleInfo {
  id: string;
  plateNumber: string;
  vin?: string;
  make?: string;
  model?: string;
  year?: number;
  displayName: string;
  isActive: boolean;
}

export interface ConnectedAccountInfo {
  accountId: string;
  displayName: string;
  email?: string;
  country?: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  isActive: boolean;
  status: string;
}

export interface TollDistributionSummary {
  totalActiveSettings: number;
  vehiclesWithDistribution: number;
  totalVehicles: number;
  averageOwnerPercentage: number;
  totalDistributedThisMonth: number;
  totalPlatformRevenueThisMonth: number;
  lastDistributionDate: string;
}

export interface TollDistributionValidationResponse {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  connectedAccountValid: boolean;
  vehicleAccessValid: boolean;
  percentageValid: boolean;
  minimumAmountValid: boolean;
}

export interface SmsLogEntry {
  id: string;
  deliveryType: string;  // 'Sms' | 'QrCode'
  messageType: string;   // 'Registration' | 'Agreement' | 'UserCreation' | ...
  phoneNumber?: string;
  bookingId?: string;
  ownerId?: string;
  carId?: string;
  renterId?: string;
  renterName?: string;
  success: boolean;
  errorMessage?: string;
  url?: string;
  initiatedBy?: string;
  createdAt: string;
}

// Driver Notes
export interface DriverNote {
  id: string;
  driverUserId: string;
  ownerUserId: string;
  ownerName: string;
  content: string;
  isOwn: boolean;
  createdAt: string;
  updatedAt: string;
}

export type { DashboardBalance, BalanceFunds, DashboardPayment, DashboardCustomer,
              DashboardPayout, DashboardConnectedAccount, DashboardPagedResult }
  from './stripeDashboard';
